import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AddFrameworkSupplierRequest,
  FrameworkAgreementDto,
  MinimalOrgDto,
  OrganizationType,
  useAddFrameworkSupplierMutation,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../common/errors/useApiError";
import { OrganizationSelector } from "../../common/organization/OrganizationSelector";
import { CreateOrganizationEntryModal } from "../organizations/entry/create-entry/CreateOrganizationEntryModal";

export const addSupplierRequest = (supplier: MinimalOrgDto): AddFrameworkSupplierRequest =>
  supplier.type === OrganizationType.OrganizationEntry
    ? { organizationEntryId: supplier.id }
    : { organizationId: supplier.id };

export const AddFrameworkSupplierModal = ({
  framework,
  onClose,
}: {
  framework: FrameworkAgreementDto;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const [add, { isLoading }] = useAddFrameworkSupplierMutation();
  const [supplier, setSupplier] = useState<MinimalOrgDto | undefined>();
  const [isCreatingEntry, setIsCreatingEntry] = useState(false);
  const onThePanel = [
    ...(framework.panel ?? []).map((member) => member.organizationId),
    ...(framework.unregisteredSuppliers ?? []).map((member) => member.organizationEntryId),
  ];

  const submit = async () => {
    if (!supplier) return;
    try {
      await add({
        frameworkAgreementId: framework.id,
        addFrameworkSupplierRequest: addSupplierRequest(supplier),
      }).unwrap();
      onClose();
    } catch (e) {
      displayer.trigger(e);
    }
  };

  if (isCreatingEntry) {
    return (
      <CreateOrganizationEntryModal
        onConfirm={async (entry) => setSupplier({ type: OrganizationType.OrganizationEntry, ...entry })}
        onSelectExisting={setSupplier}
        onClose={() => setIsCreatingEntry(false)}
      />
    );
  }

  return (
    <Modal isOpen onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg="smModalBackground">
        <ModalHeader>{t("Add supplier")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="4">
            <Text color="smMuted" fontSize="sm">
              {t(
                "The supplier gets its own supplier agreement under the framework agreement, and is told it has joined.",
              )}
            </Text>
            <FormControl isRequired>
              <FormLabel>{t("Supplier")}</FormLabel>
              <OrganizationSelector
                size="md"
                isClearable={false}
                placeholder={t("Search by name or organization number") ?? undefined}
                organization={supplier}
                orgsToExclude={[selectedOrg.id, ...onThePanel]}
                onChange={({ organization }) => setSupplier(organization)}
              />
              <FormHelperText>
                {t("Choose a registered company, or")}{" "}
                <Button
                  variant="link"
                  colorScheme="teal"
                  fontWeight="bold"
                  fontSize="sm"
                  onClick={() => setIsCreatingEntry(true)}
                >
                  {t("create a new company entry")}
                </Button>
              </FormHelperText>
              {supplier?.type === OrganizationType.OrganizationEntry && (
                <FormHelperText>
                  {t(
                    "This supplier isn't registered in SourceMagnet. It is invited to mini-competitions by e-mail, and joins the panel as its own company once it signs up.",
                  )}
                </FormHelperText>
              )}
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr="3" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button colorScheme="teal" isLoading={isLoading} isDisabled={!supplier} onClick={submit}>
            {t("Add supplier")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
